/**
 * A PHOTO, MADE SMALL BEFORE IT LEAVES THE DEVICE (Lobbero Stays plan S7.8 —
 * the guide's photos).
 *
 *   1. Anything that is not an image is refused here, before a byte is sent.
 *   2. The long edge is brought down to 1600 px (never up).
 *   3. It is encoded as WebP at 0.82. A browser that cannot encode WebP does
 *      NOT fail: Safari hands back a PNG for 'image/webp' (the trap noted in
 *      gallery/image-crop-modal.tsx), and an old WebView hands back null — so
 *      whenever the blob is not really WebP it is encoded again as JPEG at 0.85.
 *   4. If that came out LARGER than the original, and the original is an
 *      accepted type of at most 8 MB, the original is sent instead (a small,
 *      already-optimised JPEG often re-encodes bigger).
 *
 * The canvas re-encode also drops EXIF (GPS, device); the server strips
 * metadata again whatever it is sent.
 *
 * `deps` is the seam the tests use — jsdom has no canvas.
 */

export const MAX_EDGE = 1600;
export const WEBP_QUALITY = 0.82;
export const JPEG_QUALITY = 0.85;
export const MAX_BYTES = 8 * 1024 * 1024;
export const ACCEPTED_TYPES: ReadonlyArray<string> = ['image/jpeg', 'image/png', 'image/webp'];

/** The server's own sentences (lobbero_be/src/stays/routes/guide-media.js). */
export const NOT_A_PHOTO = 'Upload a JPEG, PNG or WebP photo.';
export const TOO_LARGE = 'That photo is too large. Each photo must be under 8 MB.';

export class CompressImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CompressImageError';
  }
}

export interface DecodedImage {
  width: number;
  height: number;
  source: CanvasImageSource;
  close?: () => void;
}

export interface CompressDeps {
  decode: (file: Blob) => Promise<DecodedImage>;
  encode: (image: DecodedImage, width: number, height: number, type: string, quality: number) => Promise<Blob | null>;
}

/** The size that fits `max` on the long edge, never larger than the source. */
export const fitWithin = (width: number, height: number, max: number = MAX_EDGE): { width: number; height: number } => {
  const long = Math.max(width, height);
  if (!long || long <= max) return { width, height };
  const scale = max / long;

  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

const decodeInBrowser = async (file: Blob): Promise<DecodedImage> => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);

      return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() };
    } catch {
      // Fall through to <img>: older Safari refuses some files here.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new CompressImageError(NOT_A_PHOTO));
      el.src = url;
    });

    return { width: img.naturalWidth, height: img.naturalHeight, source: img };
  } finally {
    URL.revokeObjectURL(url);
  }
};

const encodeInBrowser = (image: DecodedImage, width: number, height: number, type: string, quality: number) =>
  new Promise<Blob | null>(resolve => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);

      return;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image.source, 0, 0, width, height);
    canvas.toBlob(blob => resolve(blob), type, quality);
  });

const BROWSER: CompressDeps = { decode: decodeInBrowser, encode: encodeInBrowser };

const extensionOf = (type: string): string => (type === 'image/jpeg' ? 'jpg' : type === 'image/png' ? 'png' : 'webp');

const baseName = (name: string): string => {
  const cut = name.replace(/\.[^./\\]+$/, '');

  return cut || 'photo';
};

const asFile = (blob: Blob, name: string): File =>
  blob instanceof File ? blob : new File([blob], `${baseName(name)}.${extensionOf(blob.type)}`, { type: blob.type });

/**
 * → the File to upload. Throws CompressImageError (with words fit to show)
 * for a file that is not a photo, cannot be read, or is still over 8 MB.
 */
export const compressImage = async (file: File, deps: CompressDeps = BROWSER): Promise<File> => {
  if (!file || !/^image\//i.test(file.type || '')) throw new CompressImageError(NOT_A_PHOTO);
  const originalUsable = ACCEPTED_TYPES.includes(file.type.toLowerCase()) && file.size <= MAX_BYTES;

  let decoded: DecodedImage;
  try {
    decoded = await deps.decode(file);
  } catch {
    if (originalUsable) return file;
    throw new CompressImageError(NOT_A_PHOTO);
  }

  let out: Blob | null = null;
  try {
    const size = fitWithin(decoded.width, decoded.height);
    out = await deps.encode(decoded, size.width, size.height, 'image/webp', WEBP_QUALITY);
    if (!out || out.size === 0 || out.type !== 'image/webp') {
      out = await deps.encode(decoded, size.width, size.height, 'image/jpeg', JPEG_QUALITY);
    }
  } finally {
    if (decoded.close) decoded.close();
  }

  if (!out || out.size === 0 || !ACCEPTED_TYPES.includes(out.type)) {
    if (originalUsable) return file;
    throw new CompressImageError(NOT_A_PHOTO);
  }
  if (out.size > file.size && originalUsable) return file;
  if (out.size > MAX_BYTES) throw new CompressImageError(TOO_LARGE);

  return asFile(out, file.name);
};
